import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { LanguageContext } from "../App";

const SHOWN_KEY = "aiw_exit_intent_shown";   // sessionStorage — show once per tab
const SUBSCRIBED_KEY = "aiw_exit_subscribed";  // localStorage — never show to subscribers
const API_BASE = process.env.REACT_APP_BACKEND_URL || "https://ai-workshop-llc-backend.onrender.com";

const COPY = {
  en: {
    eyebrow: "Wait — before you go",
    title: "Get the free AI tool checklist for your industry",
    body: "5 highest-ROI AI tools for small business owners, with pricing and setup notes. One email. No spam.",
    placeholder: "your@email.com",
    submit: "Send me the checklist",
    submitting: "Sending…",
    fine: "Unsubscribe anytime.",
    successTitle: "Checklist is on the way",
    successBody: "Check your inbox in the next few minutes.",
    errorInvalid: "Please enter a valid email.",
    errorNetwork: "Couldn't reach our server — try again in a moment.",
  },
  es: {
    eyebrow: "Espera — antes de irte",
    title: "Recibe gratis la lista de herramientas AI para tu sector",
    body: "Las 5 herramientas AI con mayor ROI para dueños de pequeños negocios, con precios y notas de setup. Un solo email. Sin spam.",
    placeholder: "tu@email.com",
    submit: "Enviarme la lista",
    submitting: "Enviando…",
    fine: "Cancela la suscripción cuando quieras.",
    successTitle: "Ya va en camino",
    successBody: "Revisa tu bandeja de entrada en unos minutos.",
    errorInvalid: "Por favor ingresa un email válido.",
    errorNetwork: "No pudimos conectar con nuestro servidor — intenta de nuevo.",
  },
  tr: {
    eyebrow: "Bekleyin — ayrılmadan önce",
    title: "Sektörünüz için ücretsiz AI araç listesini alın",
    body: "Küçük işletme sahipleri için en yüksek ROI'li 5 AI aracı — fiyatlandırma ve kurulum notlarıyla birlikte. Tek email. Spam yok.",
    placeholder: "email@adresiniz.com",
    submit: "Listeyi Gönder",
    submitting: "Gönderiliyor…",
    fine: "İstediğiniz zaman abonelikten çıkabilirsiniz.",
    successTitle: "Listeniz yola çıktı",
    successBody: "Birkaç dakika içinde gelen kutunuzu kontrol edin.",
    errorInvalid: "Lütfen geçerli bir email adresi girin.",
    errorNetwork: "Sunucuya ulaşılamadı — birazdan tekrar deneyin.",
  },
};

// Derive an industry label from the current path so the capture is tagged for follow-up.
function industryFromPath(pathname) {
  const trimmed = pathname.replace(/^\/(es|tr)\//, "/").replace(/^\//, "");
  const [first] = trimmed.split("/");
  const known = new Set([
    "dental", "restaurant", "hvac", "healthcare", "legal", "real-estate",
    "retail", "shopify", "construction", "accounting", "saas",
    "auto-repair", "med-spa",
  ]);
  return known.has(first) ? first : "general";
}

export default function ExitIntentModal() {
  const { lang } = useContext(LanguageContext);
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const armedAt = useRef(Date.now());

  const c = COPY[lang] || COPY.en;

  // Trigger: mouse leaves viewport toward the top of the window (desktop only).
  // Mobile: trigger after the user has been on the page ≥20s AND scrolled ≥50%.
  useEffect(() => {
    // Suppress entirely on payment / report / form pages — those visitors are already converting.
    const path = location.pathname;
    const suppressedPrefixes = ["/payment", "/report-preview", "/full-report", "/audit",
      "/my-reports", "/subscription-success", "/payment-success", "/upgrade"];
    if (suppressedPrefixes.some((p) => path.startsWith(p))) return;
    if (sessionStorage.getItem(SHOWN_KEY) === "1") return;
    if (localStorage.getItem(SUBSCRIBED_KEY) === "1") return;

    armedAt.current = Date.now();

    const isTouch = window.matchMedia("(pointer: coarse)").matches;

    const trigger = () => {
      if (sessionStorage.getItem(SHOWN_KEY) === "1") return;
      // Require ≥4s on page before firing, so we don't flash immediately on bounce-refresh.
      if (Date.now() - armedAt.current < 4000) return;
      sessionStorage.setItem(SHOWN_KEY, "1");
      setOpen(true);
    };

    const onMouseLeave = (e) => {
      if (e.clientY <= 0) trigger();
    };

    const onScroll = () => {
      const pct = (window.scrollY + window.innerHeight) /
        Math.max(1, document.documentElement.scrollHeight);
      if (pct >= 0.5 && Date.now() - armedAt.current >= 20000) trigger();
    };

    if (isTouch) {
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => window.removeEventListener("scroll", onScroll);
    } else {
      document.addEventListener("mouseleave", onMouseLeave);
      return () => document.removeEventListener("mouseleave", onMouseLeave);
    }
  }, [location.pathname]);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error(c.errorInvalid);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed,
          source: "exit_intent",
          industry: industryFromPath(location.pathname),
        }),
      });
      if (!res.ok) throw new Error("net");
      localStorage.setItem(SUBSCRIBED_KEY, "1");
      setSent(true);
      if (typeof window.gtag !== "undefined") {
        window.gtag("event", "exit_intent_subscribe", {
          industry: industryFromPath(location.pathname),
          language: lang,
        });
      }
    } catch {
      toast.error(c.errorNetwork);
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => setOpen(false);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-sm"
      onClick={close}
    >
      <div
        className="relative w-full max-w-md bg-slate-900 border border-white/10 rounded-3xl p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          aria-label="Close"
          className="absolute top-4 right-4 text-slate-500 hover:text-slate-300 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {sent ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center mx-auto mb-5">
              <Sparkles className="w-6 h-6 text-blue-400" />
            </div>
            <h3 className="text-xl font-black text-white mb-2">{c.successTitle}</h3>
            <p className="text-slate-400 text-sm">{c.successBody}</p>
          </div>
        ) : (
          <>
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-400 uppercase tracking-widest mb-3">
              <Sparkles className="w-3.5 h-3.5" /> {c.eyebrow}
            </div>
            <h3 className="text-2xl font-black text-white leading-tight mb-3">
              {c.title}
            </h3>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              {c.body}
            </p>
            <form onSubmit={submit} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={c.placeholder}
                autoFocus
                required
                className="w-full bg-slate-950/60 border border-white/15 text-white placeholder:text-slate-500 h-12 px-4 rounded-xl text-sm focus:outline-none focus:border-blue-500/60"
              />
              <button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-gradient-to-br from-blue-500 to-blue-600 hover:shadow-blue-500/30 text-slate-950 font-black rounded-xl text-sm transition-all hover:scale-[1.01] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  c.submit
                )}
              </button>
            </form>
            <p className="text-[11px] text-slate-600 text-center mt-4">{c.fine}</p>
          </>
        )}
      </div>
    </div>
  );
}
